import type { FormItem } from 'types';
import type { ModulesAndAgendasState } from '../index';
import { toast } from 'react-toastify';
import i18next from 'i18next';
import type { LocalFormItem } from 'types/agendaForm.ts';
import type { FormItemVisibility } from 'types/formItem.ts';
import { recalcSectionItems } from 'utils/section/items.ts';
import { findTabIndex } from 'utils/section/tabSection.ts';
import { createRandomString } from 'utils/common';

export interface Payload {
  sectionId: string;
  visibilities: FormItem['visibilities'];
  currentTab: number;
  selectedItemIds?: string[];
  actionType?: 'add' | 'overwrite';
}

export const overwriteAllVisibilities = (state: ModulesAndAgendasState, { payload }: { payload: Payload }) => {
  const { sectionId, visibilities, currentTab, selectedItemIds, actionType } = payload;
  const tabSections = state.tabSections;

  const currentTabIndex = findTabIndex(tabSections, currentTab);

  if (currentTabIndex === -1) throw new Error('Invalid tab index');

  const sectionIndex = tabSections[currentTabIndex].sections.findIndex((section) => section.id === sectionId);
  if (sectionIndex === -1) throw new Error('Invalid section index');

  const section = tabSections[currentTabIndex].sections[sectionIndex];
  const formItemTabs = section.formItemTabs;

  const anyTabEmpty = formItemTabs.some((tab) => tab.items.every((item) => item.isEmpty));
  if (anyTabEmpty) {
    toast.warning(i18next.t('flow.modulesAndAgendas.form.warning.emptySection'));
    return;
  }

  section.formItemTabs = formItemTabs.map((tab) => ({
    ...tab,
    items: overwriteVisibilitiesInItems(tab.items, visibilities, selectedItemIds, actionType),
  }));

  section.items = recalcSectionItems(section.formItemTabs);
};

const overwriteVisibilitiesInItems = (
  items: LocalFormItem[],
  visibilities?: FormItemVisibility[],
  selectedItemIds?: string[],
  actionType?: 'add' | 'overwrite'
): LocalFormItem[] => {
  return items.map((item) => {
    if (item.isEmpty || !item.data) return item;

    if (selectedItemIds && selectedItemIds.length > 0 && !selectedItemIds.includes(item.identifier)) {
      return item;
    }

    let newVisibilities = visibilities;

    if (visibilities) {
      const clonedVisibilities = visibilities.map((v) => ({ ...v, id: createRandomString(10) }));

      if (actionType === 'add' && item.data.visibilities) {
        newVisibilities = [...item.data.visibilities, ...clonedVisibilities];
      } else {
        newVisibilities = clonedVisibilities;
      }
    }

    return {
      ...item,
      data: {
        ...item.data,
        isAuxiliary: false,
        visibilities: newVisibilities,
      },
    };
  });
};
